import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { BrowserNotificationsCard } from '@/features/notifications/components/browser-notifications-card';
import { SecurityActivityCard } from '@/features/user/components/security-activity-card';
import { createFileRoute } from '@tanstack/react-router';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderUserMenu } from '@/ui/components/header';
import { H1 } from '@/ui/components/heading';
import { MainContainer, MainHeader, MainScrollArea } from '@/ui/components/main';
import { UserIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { useAuth } from '@/features/auth';
import Axios from 'axios';
import { useUpdateProfile } from '@/features/user/mutations';
import { useState } from 'react';
import {
  Field,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/ui/components/form/field';
import { Button } from '@/ui/components/button';
import { Form } from '@/ui/components/form';
import { Card } from '@/ui/components/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
  SelectTriggerIcon,
} from '@/ui/components/select';
import { toastManager } from '@/ui/components/toast';
import { tryCatch } from '@/try-catch';
import { CountryFlag } from '@/ui/components/country-flag';
import { type Locale, LOCALE_COUNTRY_CODES } from '@/features/i18n/constants';

export const Route = createFileRoute('/_authed/settings/profile')({
  component: RouteComponent,
});

const CURRENTLY_SUPPORTED_LANGUAGES = ['en-US', 'nb-NO'] as const;

function RouteComponent() {
  const auth = useAuth();
  const { firstName, lastName, email, systemLanguage, defaultTranslationLanguage } = auth.user;

  const [firstNameValue, setFirstNameValue] = useState(firstName);
  const [lastNameValue, setLastNameValue] = useState(lastName);
  const [systemLanguageValue, setSystemLanguageValue] = useState(systemLanguage ?? 'en-US');
  const [resetSent, setResetSent] = useState(false);
  const [resetPending, setResetPending] = useState(false);

  const { isPending, mutate } = useUpdateProfile();

  return (
    <>
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={UserIcon} />
            {t({ en: 'Profile', nb: 'Profil' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>{t({ en: 'Profile', nb: 'Profil' })}</H1>
        </MainHeader>
        <MainContainer className="max-w-xl space-y-6">
          <Card className="p-4">
            <Form
              onFormSubmit={() =>
                mutate({
                  firstName: firstNameValue,
                  lastName: lastNameValue,
                  systemLanguage: systemLanguageValue,
                  defaultTranslationLanguage,
                })
              }
              className="space-y-6"
            >
              <div className="grid grid-cols-2 gap-x-5">
                <Field>
                  <FieldLabel>{t({ en: 'First name', nb: 'Fornavn' })}</FieldLabel>
                  <FieldControl
                    required
                    maxLength={40}
                    value={firstNameValue}
                    onValueChange={setFirstNameValue}
                    placeholder={t({ en: 'First name', nb: 'Fornavn' })}
                  />
                  <FieldError />
                </Field>

                <Field>
                  <FieldLabel>{t({ en: 'Last name', nb: 'Etternavn' })}</FieldLabel>
                  <FieldControl
                    required
                    maxLength={40}
                    value={lastNameValue}
                    onValueChange={setLastNameValue}
                    placeholder={t({ en: 'Last name', nb: 'Etternavn' })}
                  />
                  <FieldError />
                </Field>
              </div>

              <Field>
                <FieldLabel>{t({ en: 'Email address', nb: 'E-postadresse' })}</FieldLabel>
                <FieldControl required value={email} readOnly />
                <FieldError />
              </Field>

              <div className="space-y-2">
                <Field>
                  <FieldLabel>{t({ en: 'Password', nb: 'Passord' })}</FieldLabel>
                  <FieldControl type="password" value="**********" readOnly />
                </Field>

                <Button
                  type="button"
                  variant="secondary"
                  disabled={resetSent}
                  pending={resetPending}
                  onClick={async () => {
                    setResetPending(true);
                    const { error } = await tryCatch(
                      Axios.post(
                        `https://${import.meta.env.VITE_AUTH0_DOMAIN}/dbconnections/change_password`,
                        {
                          client_id: import.meta.env.VITE_AUTH0_CLIENT_ID,
                          connection: import.meta.env.VITE_AUTH0_CONNECTION,
                          email,
                        },
                      ),
                    );
                    setResetPending(false);
                    if (error)
                      return toastManager.add({
                        type: 'error',
                        title: t(common.notifications.error),
                      });
                    setResetSent(true);
                  }}
                >
                  {resetSent
                    ? t({
                        en: 'Reset password link sent!',
                        nb: 'Lenke for tilbakestilling av passord sendt!',
                      })
                    : t({
                        en: 'Send me a password reset link',
                        nb: 'Send meg en lenke for tilbakestilling av passord',
                      })}
                </Button>
              </div>

              <Field>
                <FieldLabel>{t({ en: 'System language', nb: 'Systemspråk' })}</FieldLabel>
                <Select
                  required
                  value={systemLanguageValue}
                  onValueChange={(v) => {
                    if (!v) return;
                    setSystemLanguageValue(v);
                  }}
                >
                  <SelectTrigger className="w-full pl-2">
                    <SelectValue>
                      {(v) => (
                        <>
                          <CountryFlag code={LOCALE_COUNTRY_CODES[v as Locale]} />
                          {t(common.languages[v as Locale])}
                        </>
                      )}
                    </SelectValue>
                    <SelectTriggerIcon />
                  </SelectTrigger>
                  <SelectPopup>
                    {CURRENTLY_SUPPORTED_LANGUAGES.map((lang) => (
                      <SelectItem key={lang} value={lang}>
                        <CountryFlag code={LOCALE_COUNTRY_CODES[lang]} />
                        {t(common.languages[lang])}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
                <FieldDescription>
                  {t({
                    en: 'This is the language that will be used throughout the whole application.',
                    nb: 'Dette er språket som vil bli brukt i hele appen.',
                  })}
                </FieldDescription>
                <FieldError />
              </Field>
              <div className="flex justify-end pt-2">
                <Button type="submit" pending={isPending}>
                  {t({ en: 'Update profile', nb: 'Oppdater profil' })}
                </Button>
              </div>
            </Form>
          </Card>
          <SecurityActivityCard />
          <BrowserNotificationsCard />
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
